import { apiService } from './api';
import { clearAuthData } from '../utils/storage';
import { 
  User, 
  LoginRequest, 
  LoginResponse, 
  RegisterRequest, 
  ChangePasswordRequest 
} from '../types/auth';

class AuthService {
  setToken(token: string) {
    apiService.setToken(token);
  }

  removeToken() {
    apiService.removeToken();
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      // Clear any existing auth data before attempting to log in
      clearAuthData();
      
      const data: LoginRequest = { email, password };
      
      // Create a clean request with minimal data
      const response = await apiService.post<LoginResponse>('/auth/login', data);

      // Handle different possible response formats (apiService returns payload, not AxiosResponse)
      const responseData: any = response;

      if (!responseData) {
        throw new Error('Empty response from server');
      }

      // Extract token and user from different possible response formats
      const token = responseData.token || responseData.access_token || responseData.accessToken;
      const user = responseData.user || responseData.data?.user;

      if (!token) {
        throw new Error('No authentication token received');
      }

      return {
        message: responseData.message || 'Login successful',
        token,
        user: user || { email } // Fallback to minimal user data if not provided
      };
      
    } catch (error: any) {
      console.error('AuthService login error:', error);
      
      // Clean up any potentially problematic data
      clearAuthData();
      
      // Extract meaningful error message
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.response) {
        // Server responded with an error status code
        const { status, data } = error.response;
        
        if (status === 400 || status === 401) {
          errorMessage = data?.message || 'Invalid email or password';
        } else if (status === 431) {
          errorMessage = 'Authentication error. Please clear your browser data and try again.';
        } else {
          errorMessage = data?.message || `Server error (${status})`;
        }
      } else if (error.request) {
        // Request was made but no response was received
        errorMessage = 'Cannot connect to the server. Please check your internet connection.';
      } else if (error.message) {
        // Something happened in setting up the request
        errorMessage = error.message;
      }
      
      // Log the specific error for debugging
      console.error('Login failed with error:', errorMessage);
      
      throw new Error(errorMessage);
    }
  }

  async register(userData: RegisterRequest): Promise<LoginResponse> {
    const response = await apiService.post<any>('/auth/register', userData);
    // Handle the backend response format
    return {
      message: response.message,
      token: response.token,
      user: response.user
    };
  }

  async getCurrentUser(): Promise<User> {
    const response = await apiService.get<{ user: User }>('/auth/me');
    return response.user;
  }

  async changePassword(passwords: ChangePasswordRequest): Promise<void> {
    await apiService.put('/auth/change-password', passwords);
  }

  async refreshToken(): Promise<{ token: string }> {
    return apiService.post<{ token: string }>('/auth/refresh');
  }

  async logout(): Promise<void> {
    try {
      // Try to call the logout endpoint if it exists
      try {
        await apiService.post('/auth/logout');
      } catch (e) {
        // Ignore errors from the logout endpoint
        console.debug('Logout endpoint not available or failed', e);
      }
    } finally {
      // Always clear all auth data
      clearAuthData();
      this.removeToken();
      
      // Clear any in-memory data
      if (typeof window !== 'undefined') {
        // Force a full page reload to clear any application state
        window.location.href = '/login';
      }
    }
  }
}

export const authService = new AuthService();
